import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  adminSearchRealmUsers,
  type RealmUserSearchResult,
} from '@frontend/api-clients/adminUserClient';

function formatRealmUserLabel(u: RealmUserSearchResult): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (name) return `${name} · ${u.username}`;
  return u.username || u.email || u.userId;
}

export type RealmUserPickerProps = {
  id: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Controlled selection; null = none / typing */
  value: RealmUserSearchResult | null;
  onChange: (user: RealmUserSearchResult | null, query: string) => void;
  /** When true, parent may submit on typed email without picking a row */
  allowEmailFallback?: boolean;
};

/**
 * Anchored combobox for existing Keycloak realm users (DASH-021 unified control).
 * Dropdown opens from the input; Create/Invite stay on Identity widgets.
 */
export default function RealmUserPicker({
  id,
  label,
  placeholder = 'username, email, or name',
  disabled = false,
  value,
  onChange,
  allowEmailFallback = true,
}: RealmUserPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const blurCloseTimer = useRef<number | null>(null);

  const [query, setQuery] = useState(() =>
    value ? value.email || value.username || '' : ''
  );
  const [results, setResults] = useState<RealmUserSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const selectedUserId = value?.userId;
  const selectedEmail = value?.email;
  const selectedUsername = value?.username;
  useEffect(() => {
    if (selectedUserId) {
      setQuery(selectedEmail || selectedUsername || '');
    }
  }, [selectedUserId, selectedEmail, selectedUsername]);

  useEffect(() => {
    const token = authClient.getToken();
    if (!token || disabled) return;

    const handle = window.setTimeout(() => {
      setSearchBusy(true);
      setSearchError('');
      void adminSearchRealmUsers(token, query, 25)
        .then((users) => {
          setResults(users);
          setHighlightIndex(users.length > 0 ? 0 : -1);
        })
        .catch((err) => {
          setResults([]);
          setSearchError(err instanceof Error ? err.message : 'Failed to search realm users');
        })
        .finally(() => setSearchBusy(false));
    }, 250);

    return () => window.clearTimeout(handle);
  }, [query, disabled]);

  useEffect(() => {
    return () => {
      if (blurCloseTimer.current) window.clearTimeout(blurCloseTimer.current);
    };
  }, []);

  const selectUser = (user: RealmUserSearchResult) => {
    const nextQuery = user.email || user.username || '';
    setQuery(nextQuery);
    onChange(user, nextQuery);
    setOpen(false);
    setHighlightIndex(-1);
  };

  const onInputChange = (next: string) => {
    setQuery(next);
    onChange(null, next);
    setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      if (results.length === 0) return;
      setHighlightIndex((i) => (i + 1) % results.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (results.length === 0) return;
      setHighlightIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
      return;
    }
    if (event.key === 'Enter' && open && highlightIndex >= 0 && results[highlightIndex]) {
      event.preventDefault();
      selectUser(results[highlightIndex]);
    }
  };

  const showMenu = open && !disabled;
  const emptyHint = allowEmailFallback
    ? 'No matching users. Refine the search or paste a full email.'
    : 'No matching users. Refine the search.';

  return (
    <div className="realm-user-picker" ref={rootRef}>
      {label ? (
        <label className="admin-orch-field" htmlFor={id}>
          <span>{label}</span>
        </label>
      ) : null}
      <div className="realm-user-picker-field">
        <input
          id={id}
          type="search"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={showMenu}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightIndex >= 0 && results[highlightIndex]
              ? `${listId}-opt-${results[highlightIndex].userId}`
              : undefined
          }
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurCloseTimer.current = window.setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={onKeyDown}
        />
        {showMenu ? (
          <ul id={listId} className="realm-user-picker-menu" role="listbox">
            {searchBusy ? (
              <li className="realm-user-picker-status">Searching…</li>
            ) : searchError ? (
              <li className="realm-user-picker-status realm-user-picker-status-error">{searchError}</li>
            ) : results.length === 0 ? (
              <li className="realm-user-picker-status">{emptyHint}</li>
            ) : (
              results.map((u, index) => (
                <li key={u.userId} role="presentation">
                  <button
                    type="button"
                    id={`${listId}-opt-${u.userId}`}
                    className={`admin-orch-picker-item${
                      value?.userId === u.userId || highlightIndex === index ? ' is-selected' : ''
                    }`}
                    role="option"
                    aria-selected={value?.userId === u.userId || highlightIndex === index}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectUser(u)}
                    onMouseEnter={() => setHighlightIndex(index)}
                  >
                    <strong>{formatRealmUserLabel(u)}</strong>
                    <span>
                      {u.email || 'no email'}
                      {u.enabled === false ? ' · disabled' : ''}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {value ? (
        <p className="ecosystem-description realm-user-picker-selected">
          Selected: <strong>{formatRealmUserLabel(value)}</strong>
          {value.email ? ` · ${value.email}` : ''}
        </p>
      ) : null}
    </div>
  );
}
